import { useCallback, useEffect, useRef, useState } from "react";
import { trackAdminUiEvent, ADMIN_UI_EVENTS } from "../utils/adminAnalytics";

/**
 * useSetupCallPrompt — decides WHEN to open the proactive setup-call modal.
 *
 * ══ WHY THIS IS NOT A `setTimeout(…, 5000)` ══
 *
 * The first draft was "fire 5-11s after paint, with jitter". Both halves are
 * contradicted by the interruption literature:
 *
 *   • A fixed timer is the WORST-performing coordination method tested. McFarlane
 *     (2002, Human-Computer Interaction 17(1), N=36) put "Scheduled" in the worst
 *     column for accuracy, efficiency, user preference AND perceived interruptiveness,
 *     usually tied with interrupting immediately.
 *   • Jitter has nothing behind it. Adamczyk & Bailey (CHI 2004) measured random
 *     timing directly: boundary-triggered beat random by 43% less annoyance and 27%
 *     higher perceived "respect". Random beat the worst moment and lost to a boundary.
 *
 * So this implements BOUNDED DEFERRAL (Horvitz, Apacible & Subramani, UM 2005): fire at
 * a task boundary; the timer is only a CEILING guaranteeing eventual delivery, never the
 * trigger. Boundaries, in priority order:
 *
 *   1. The campaign editor just closed — a real task completion, the strongest signal.
 *      (The server applies a 10-minute post-build cooldown, so this only fires for a
 *      merchant who opened the editor and BACKED OUT — exactly the target population.)
 *   2. Dashboard settled AND hands still for IDLE_MS — "page finished, not typing".
 *   3. CEILING_MS elapsed — the safety valve, so a slow reader still gets the offer.
 */

const IDLE_MS = 1500;
const CEILING_MS = 20000;
/**
 * How long to wait before retrying after a SUPPRESSED attempt (another modal was open, the
 * campaign editor was up, an error banner was showing). Deliberately not the full ceiling:
 * suppression is usually short-lived, and the merchant who was busy at that exact instant
 * should not lose the offer for the rest of the session.
 */
const SUPPRESSED_RETRY_MS = 8000;

/**
 * Once-per-session guard, scoped by shop id.
 *
 * ⚠️ sessionStorage is the FAST guard, never the real one. It is per-TAB (two admin tabs
 * would each fire) and can be denied outright in a cross-site admin iframe under Safari
 * ITP. The real guarantee is the 2-hour server-side `setupCallPromptLastShownAt` floor in
 * checkSetupCallEligibility — which is why these reads FAIL OPEN: the server has already
 * said yes, and it will not say yes again for two hours.
 *
 * Scoped by shop id because sessionStorage is per-origin, and one tab navigated between
 * two of a merchant's shops would otherwise share the flag.
 */
const sessionKey = (shopId) => `nudgesmart_setup_call_prompt_v1:${shopId}`;

function alreadyShownThisSession(shopId) {
  try {
    return sessionStorage.getItem(sessionKey(shopId)) !== null;
  } catch {
    return false; // fail OPEN — see above
  }
}

function markShownThisSession(shopId) {
  try {
    sessionStorage.setItem(sessionKey(shopId), "1");
  } catch {
    /* storage denied; the server cooldown still holds */
  }
}

/**
 * @param {object}  opts
 * @param {object}  opts.api             Gadget API client
 * @param {string}  opts.shopId
 * @param {boolean} opts.paintGateOpen   this route's own "dashboard has painted" gate
 * @param {boolean} opts.editorOpen      App Bridge max-modal editor is open
 * @param {() => boolean} opts.isSuppressed  any other reason not to fire. A CALLBACK, not
 *   a boolean: "is another modal open?" can only be answered by reading the DOM at FIRE
 *   time. A boolean computed during render is stale by the time the deferral resolves.
 */
export function useSetupCallPrompt({ api, shopId, paintGateOpen, editorOpen, isSuppressed }) {
  const [open, setOpen] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  /**
   * Bumped to re-ask the server whether to prompt. The answer is time-sensitive — the
   * post-build and 2-hour cooldowns are both evaluated server-side at fetch time — so any
   * event that could change it must re-fetch rather than reuse the cached answer.
   */
  const [recheckToken, setRecheckToken] = useState(0);

  /**
   * Has this mount already put the prompt in front of the merchant?
   *
   * ⚠️ IN MEMORY, deliberately — this is NOT a duplicate of the sessionStorage guard.
   * Storage can be denied outright in a cross-site admin iframe (Safari ITP), and those
   * reads fail OPEN by design, so sessionStorage cannot be the thing that stops a second
   * fire. Memory cannot be denied. sessionStorage carries the decision ACROSS page loads;
   * this ref carries it across the current one.
   */
  const firedThisMountRef = useRef(false);


  /**
   * Monotonic latch. `paintGateOpen` can flip back to false when a `live` query refetches;
   * without the latch a refetch storm restarts the deferral forever and the prompt starves.
   */
  const [hasPainted, setHasPainted] = useState(false);
  useEffect(() => {
    if (paintGateOpen) setHasPainted(true);
  }, [paintGateOpen]);

  /**
   * Live values the DEFERRED callback must read fresh. A `[]`-deps effect captures the
   * first render's values, and these all change after that — so they go in a ref that is
   * reassigned on every render, never in the dep array.
   */
  const suppressRef = useRef(null);
  suppressRef.current = () => {
    if (editorOpen) return true;
    try {
      return typeof isSuppressed === "function" ? Boolean(isSuppressed()) : false;
    } catch {
      return true; // unreadable state -> assume busy; never interrupt on a guess
    }
  };

  const eligibilityRef = useRef(null);
  eligibilityRef.current = eligibility;

  // ── Ask the server whether to prompt at all, and with which message ──────────────
  //
  // Deliberately NOT gated behind the deferral: the answer is "no" for most shops most
  // of the time, and knowing that early means we never arm a single timer for them.
  useEffect(() => {
    if (!hasPainted || !shopId) return undefined;
    if (firedThisMountRef.current) return undefined;
    if (alreadyShownThisSession(shopId)) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.shopifyShop.checkSetupCallEligibility(shopId);
        if (!cancelled) setEligibility(res?.eligible ? res : null);
      } catch {
        // Non-critical. A failed eligibility check must never disturb the dashboard.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasPainted, shopId, api, recheckToken]);

  /**
   * Stamp the server-side floor and advance the rotation.
   *
   * Fire-and-forget: if it fails the merchant simply sees the prompt again sooner, which
   * is far better than blocking the UI on a bookkeeping write. This is the ONLY place the
   * outcome is recorded, so every path that puts the prompt in front of a merchant —
   * automatic or manual — goes through it and cannot forget the cooldown.
   *
   * Takes ONLY the outcome. `recordSetupCallPromptOutcome` declares no `variant` param, so
   * an extra argument would be silently discarded and read to a future maintainer as
   * "the variant is recorded server-side" when it is not.
   */
  const commitOutcome = useCallback(
    (outcome) => {
      if (!shopId) return;
      api.shopifyShop.recordSetupCallPromptOutcome(shopId, { outcome }).catch(() => {});
    },
    [api, shopId]
  );

  /**
   * Attempt to show the prompt.
   *
   * ⚠️ TRI-STATE, and the distinction is load-bearing. A boolean conflates "busy right
   * now, try again" with "never going to happen", and the caller re-arms on false — so
   * an already-shown prompt would re-arm every 8s for the life of the mount, keeping
   * three input listeners bound and running a DOM scan each time. Roughly 10,800 wake-ups
   * in a tab left open for a day.
   *
   * @returns {"fired"|"suppressed"|"done"}
   *   fired     — shown (or deliberately consumed as a quiet session); stop.
   *   suppressed — momentarily busy; the caller should retry.
   *   done      — nothing more to do this mount; stop, do NOT retry.
   */
  const fire = useCallback(() => {
    const elig = eligibilityRef.current;
    if (!elig || !shopId) return "done";
    if (firedThisMountRef.current) return "done";
    if (alreadyShownThisSession(shopId)) return "done";
    if (suppressRef.current()) return "suppressed";

    // Quiet session: this segment alternates modal / no-modal so a merchant who is not in
    // the urgent segments is interrupted every OTHER visit rather than every visit. The
    // offer is still on screen — SupportHub's card is permanent — this only skips the
    // interruption. Recorded anyway so the cycle advances and the next visit shows it.
    const quiet = !elig.skipQuietSession && (elig.cyclePosition ?? 0) % 2 === 1;

    firedThisMountRef.current = true;
    markShownThisSession(shopId);
    commitOutcome(quiet ? "quiet" : "shown");
    if (quiet) return "fired";

    setOpen(true);
    trackAdminUiEvent(api, ADMIN_UI_EVENTS.SETUP_CALL_PROMPT_SHOWN, { variant: elig.variant });
    return "fired";
  }, [api, shopId, commitOutcome]);

  // ── Bounded deferral: idle boundary + ceiling ────────────────────────────────────
  //
  // StrictMode-safe by construction: real deps, NO once-ref, and cleanup only tears down
  // timers and listeners. Cleanup #1 tears down, setup #2 re-arms cleanly. There is
  // deliberately no `cancelled` flag — a once-ref plus a cancelled flag is the documented
  // deadlock, where cleanup sets cancelled, the ref blocks the re-run, and the work never
  // completes.
  useEffect(() => {
    if (!hasPainted || !eligibility) return undefined;

    let idleTimer = null;
    let ceilingTimer = null;
    let finished = false;

    // ⚠️ The listeners MUST be removed once we have fired, not just the timers. Leaving
    // them bound means the next pointer move re-arms the idle timer, which fires again,
    // forever — one scheduled timeout per mouse move for the life of the mount. And if
    // sessionStorage is unavailable (Safari ITP denies it to a cross-site admin iframe,
    // and these reads deliberately fail OPEN) the once-per-session guard cannot stop it
    // either: the modal reopens ~every 1.5s, un-dismissable, writing to shopifyShop each
    // time. The effect's deps never change, so its own cleanup would never run.
    const teardown = () => {
      clearTimeout(idleTimer);
      clearTimeout(ceilingTimer);
      window.removeEventListener("pointermove", resetIdle);
      window.removeEventListener("keydown", resetIdle);
      window.removeEventListener("scroll", resetIdle);
    };

    const attempt = () => {
      if (finished) return;
      const result = fire();
      // "done" means nothing more will happen this mount — tear down exactly as if it
      // had fired, or the listeners stay bound and the retry runs forever.
      if (result === "fired" || result === "done") {
        finished = true;
        teardown();
        return;
      }
      // Suppressed (another modal, the editor, an error banner). Do NOT consume the
      // ceiling — re-arm it, or a merchant who happened to be busy at that exact instant
      // is starved for the rest of the session. The idle listeners stay bound so any
      // input also retries.
      clearTimeout(ceilingTimer);
      ceilingTimer = setTimeout(attempt, SUPPRESSED_RETRY_MS);
    };

    function resetIdle() {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(attempt, IDLE_MS);
    }

    // Passive: these must never delay the merchant's own scrolling or typing.
    window.addEventListener("pointermove", resetIdle, { passive: true });
    window.addEventListener("keydown", resetIdle, { passive: true });
    window.addEventListener("scroll", resetIdle, { passive: true });

    resetIdle();
    ceilingTimer = setTimeout(attempt, CEILING_MS);

    return teardown;
  }, [hasPainted, eligibility, fire]);

  // ── Boundary 1: the campaign editor just closed ─────────────────────────────────
  //
  // The same signal as the suppression above, read in the opposite direction: suppress
  // while OPEN, act on CLOSE. Order matters, hence the previous-value ref rather than a
  // plain `!editorOpen` test (which would be true on first render too).
  //
  // ⚠️ This RE-CHECKS eligibility instead of firing directly, and that is the whole point
  // of the boundary. Eligibility was fetched once at paint — BEFORE the merchant opened
  // the editor — so the server's 10-minute post-build cooldown was evaluated against a
  // world where they had not built anything yet. Firing on that stale object offers to
  // "build your first campaign with you" seconds after they built one, which is exactly
  // what the cooldown exists to prevent. Bumping the token re-runs the fetch; the server
  // then sees the fresh campaign and answers `recent-build`.
  const prevEditorOpen = useRef(Boolean(editorOpen));
  useEffect(() => {
    const was = prevEditorOpen.current;
    prevEditorOpen.current = Boolean(editorOpen);
    if (was && !editorOpen && !firedThisMountRef.current) {
      setRecheckToken((n) => n + 1);
    }
  }, [editorOpen]);

  // ── If the editor opens while we are on screen, get out of the way ───────────────
  // The merchant's own work always wins.
  useEffect(() => {
    if (editorOpen && open) setOpen(false);
  }, [editorOpen, open]);

  const dismiss = useCallback(() => {
    setOpen(false);
    trackAdminUiEvent(api, ADMIN_UI_EVENTS.SETUP_CALL_PROMPT_DISMISSED, {
      variant: eligibilityRef.current?.variant,
    });
  }, [api]);


  /**
   * The merchant booked. Close and stop asking for the rest of the session — the durable
   * stop is `setupCallBookedAt`, written server-side by the Cal.com webhook.
   */
  const onBooked = useCallback(() => {
    setEligibility(null);
    setOpen(false);
  }, []);

  /**
   * Manual open — SupportHub's "Build it with me" button.
   *
   * ⚠️ Runs the SAME bookkeeping as an automatic fire. Without it the deferral is still
   * armed behind the modal the merchant just opened themselves, and ~1.5s later it fires
   * a spurious `ns_setup_call_prompt_shown` plus a server cooldown stamp for a prompt that
   * was never automatic. `isSetupCallSuppressed` cannot catch that, because it explicitly
   * excludes our own modal from its open-modal scan.
   *
   * Emits `_clicked` with source "support_hub", never `_shown` — a merchant-initiated open
   * is not an impression of the prompt, and counting it as one would inflate the
   * denominator of the funnel this feature is measured by.
   */
  const openManually = useCallback(() => {
    setOpen(true);
    // Only bookkeep when a prompt was actually pending. Recording unconditionally would
    // _atomics-increment setupCallPromptShownCount for a prompt that was never shown, and
    // advance the quiet-session rotation on a merchant-initiated click — polluting both
    // the impression counter and the alternation the counter drives.
    if (eligibilityRef.current && shopId) {
      firedThisMountRef.current = true;
      markShownThisSession(shopId);
      commitOutcome("clicked");
    }
    trackAdminUiEvent(api, ADMIN_UI_EVENTS.SETUP_CALL_PROMPT_CLICKED, {
      source: "support_hub",
      variant: eligibilityRef.current?.variant ?? "none",
    });
  }, [api, shopId, commitOutcome]);

  return { open, eligibility, dismiss, onBooked, openManually };
}

export default useSetupCallPrompt;
